import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	App,
	Button,
	ConfigProvider,
	List,
	Popconfirm,
	Tag,
	Typography,
	Upload,
} from 'antd';

import { DataState, Skeleton, deriveDataState } from '../loading';
import { normalizeId } from '../../utils/ids';
import { formatBytes } from '../../utils/formatters';
import { joinMeta } from '../../utils/counts';
import { formatShortDate } from '../../utils/dateDisplay';
import { isAttachAdmin } from '../../utils';
import {
	ATTACHMENT_MAX_MB,
	ATTACHMENT_MAX_BYTES,
	getFileExtension,
	getSubjectService,
	isBlockedFileName,
} from '../../services/attachments';
import {
	CLOSE,
	DOWNLOAD,
	FILE,
	FILE_CSV,
	FILE_DOC,
	FILE_DOCX,
	FILE_IMAGE,
	FILE_MD,
	FILE_PDF,
	FILE_PPT,
	FILE_PPTX,
	FILE_TXT,
	FILE_XLS,
	FILE_XLSX,
	FILE_ZIP,
	PAPERCLIP,
} from '../icons';
import AttachmentPreviewModal, { canOpenAttachment } from './AttachmentPreviewModal';
import { destructivePopconfirmProps } from './confirmDestructive';
import useAttachmentVariant from '../../hooks/useAttachmentVariant';
import { forgetAttachmentVariants, hasAttachmentVariant } from '../../utils/attachmentVariants';
import { publicDownloadLines } from '../../utils/publicFiles';
import FileScanNote, { confirmFlaggedFile, confirmFlaggedFiles } from './FileScanNote';
import { anchorOf } from '../../platform';

const { Text } = Typography;

// Extension → icon. Anything unmapped falls back to the generic file page, so
// a new file type is a missing icon, never a missing row.
const FILE_TYPE_ICONS = {
	pdf: FILE_PDF,
	doc: FILE_DOC,
	docx: FILE_DOCX,
	xls: FILE_XLS,
	xlsx: FILE_XLSX,
	csv: FILE_CSV,
	ppt: FILE_PPT,
	pptx: FILE_PPTX,
	txt: FILE_TXT,
	md: FILE_MD,
	zip: FILE_ZIP,
	gz: FILE_ZIP,
	tar: FILE_ZIP,
	rar: FILE_ZIP,
	'7z': FILE_ZIP,
	png: FILE_IMAGE,
	jpg: FILE_IMAGE,
	jpeg: FILE_IMAGE,
	gif: FILE_IMAGE,
	webp: FILE_IMAGE,
	heic: FILE_IMAGE,
	svg: FILE_IMAGE,
};

/**
 * Shared by the attachments list and the admin attachments table so one file
 * type never reads two ways across the app.
 */
export function getFileTypeIcon(ext) {
	const key = `${ext || ''}`.toLowerCase().replace(/^\./, '');
	return FILE_TYPE_ICONS[key] || FILE;
}

/**
 * An attachment no file element holds (`element` null — or absent, from an
 * API before PR 10b): the "Other attachments" this section lists.
 */
export const isOtherAttachment = attachment => attachment?.element === null || attachment?.element === undefined;

// Downloads are sequential: browsers throttle (and some block) a burst of
// programmatic saves, and each one is a separate authenticated round trip.
const DOWNLOAD_ALL_DELAY_MS = 400;
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

// Server errors carry their own words (which size limit, which type) — show
// them rather than a generic failure, but never render a non-string body.
const serverMessage = (error) => {
	const data = error?.response?.data;
	if (data && typeof data === 'object' && typeof data.message === 'string') return data.message;
	if (typeof data === 'string' && data.trim()) return data;
	return null;
};

/**
 * The row's picture: the server's 160 px `sm` thumbnail when the attachment
 * has one (a photo, or a PDF's first page), the type icon otherwise.
 *
 * Fetched through the authenticated client as a blob (the variants route
 * needs the JWT), held in the shared cache so a re-render asks nothing, and
 * never the original: a list of fifty photos is fifty small WebPs, not fifty
 * downloads. While it loads, and if it fails or the bytes will not decode,
 * the row shows the type icon — silently, as it did before thumbnails.
 *
 * The thumbnail opens the viewer too, for a pointer or a finger; it is a
 * second door to the name button, so it stays out of the tab order and the
 * accessibility tree (the name is the one the keyboard and a screen reader
 * use).
 */
function AttachmentRowPicture({ itemId, attachment, TypeIcon, onOpen }) {
	const thumb = useAttachmentVariant(itemId, attachment, 'sm');
	const [broken, setBroken] = useState(false);

	if (!hasAttachmentVariant(attachment, 'sm') || !itemId || thumb.failed || broken) {
		return (
			<span className="attachments__icon" aria-hidden="true">
				<TypeIcon />
			</span>
		);
	}

	const picture = thumb.url ? (
		<img
			className="attachments__thumb-img"
			src={thumb.url}
			alt=""
			draggable={false}
			onError={() => setBroken(true)}
		/>
	) : (
		<TypeIcon />
	);

	if (!onOpen) {
		return <span className="attachments__thumb" aria-hidden="true">{picture}</span>;
	}
	return (
		<button
			type="button"
			className="attachments__thumb attachments__thumb--button"
			aria-hidden="true"
			tabIndex={-1}
			onClick={onOpen}
		>
			{picture}
		</button>
	);
}

/**
 * The "Attachments" section of a record form.
 *
 * Like tags and related items, attachments are their own records: uploads and
 * removals hit the API the moment they happen rather than waiting on the
 * form's Save button, so the section needs a SAVED subject to hang off (a
 * brand-new item has no id yet — hence the save-first guard).
 *
 * `subjectType` selects the service functions (see ATTACHMENT_SUBJECTS); only
 * 'item' is wired today.
 *
 * `variant='embedded'` is for surfaces that frame the section themselves (the
 * read view's meta accordion, the Attachments modal): no heading, the
 * Download all action right-aligned. `onCountChange` reports how many files
 * there are, for a header badge.
 *
 * `locked` is the record lock (`utils/recordPolicy` lockStateFor): on a record
 * its card has locked, the API refuses an upload and a ✕ with 409
 * `item_locked` exactly as it refuses an item edit (the owner's ruling,
 * 2026-10-02), so neither is offered — the dropzone's place says why in one
 * line — until a manager's unlock. A refusal that still arrives (the lock
 * closed while the page was open) shows the server's own words.
 *
 * Each row says its malware verdict (PR 10e) under its facts — nothing when
 * clean, "Not checked yet" quietly, the warning line when flagged — and a
 * flagged file's Download (and Download all, once, when any is flagged)
 * asks first. Told, never blocked.
 *
 * There is no Attach button: the dashed dropzone is the affordance (and the
 * empty line's "Attach a file" link opens the same picker). Download all
 * exists only once there is something to download (and only for a reader
 * who may take copies — `canDownload`).
 */
export default function AttachmentsSection({
	subjectType = 'item',
	subjectId,
	readOnly = false,
	// The record is locked to this viewer's changes (see above): reading and
	// downloading stay, Attach and Remove go.
	locked = false,
	// False for a reader who may see the files but not take them — a
	// link-only reader whose link does not allow copies (keepr-api
	// § Collection links: attachment downloads are 403 copies_not_allowed).
	// Download, Download all and Preview (which reads the download
	// endpoint) go; the list stays.
	canDownload = true,
	title = 'Attachments',
	variant = 'panel',
	onCountChange,
}) {
	const { message, modal } = App.useApp();
	const onCountChangeRef = useRef(onCountChange);
	onCountChangeRef.current = onCountChange;

	const [attachments, setAttachments] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState(false);
	// The subject whose list is on screen: a list read for another record is not ours to show.
	const [loadedFor, setLoadedFor] = useState(null);
	const [attempt, setAttempt] = useState(0);
	const [uploading, setUploading] = useState(false);
	const [busyId, setBusyId] = useState(null);
	const [downloadingAll, setDownloadingAll] = useState(false);
	// The row the preview modal is open on; null closes it. One modal for the
	// whole section, so a stale file can never surface under a new title.
	const [previewing, setPreviewing] = useState(null);
	const mountedRef = useRef(false);

	const service = getSubjectService(subjectType);

	const resolvedSubjectId = useMemo(() => {
		const normalized = normalizeId(subjectId);
		return normalized && normalized !== '-1' ? normalized : null;
	}, [subjectId]);

	useEffect(() => {
		mountedRef.current = true;
		return () => { mountedRef.current = false; };
	}, []);

	// A subject that changes (or a section that unmounts) cancels the list it
	// asked for: an answer for the previous record must never land under the
	// next one, and a request nobody will read is not sent to completion.
	const load = useCallback(async ({ signal } = {}) => {
		if (!resolvedSubjectId || !service) {
			// A subject that went away mid-read: the read was cancelled, so
			// its own `finally` will not stop the skeleton.
			setAttachments([]);
			setLoading(false);
			setLoadError(false);
			return;
		}
		setLoading(true);
		setLoadError(false);
		try {
			const data = await service.list(resolvedSubjectId, { signal });
			if (!mountedRef.current || signal?.aborted) return;
			// Other attachments only (PR 10b): a file a file element holds is
			// that element's value — shown, replaced and removed in the item's
			// fields — never listed here, where its ✕ would change the value
			// outside the item's save (the API answers 409 attachment_in_use).
			setAttachments((data || []).filter(isOtherAttachment));
			setLoadedFor(resolvedSubjectId);
		} catch (error) {
			// Cancelled, not refused: nothing to say.
			if (signal?.aborted) return;
			console.error(error);
			if (mountedRef.current) setLoadError(true);
		} finally {
			if (mountedRef.current && !signal?.aborted) setLoading(false);
		}
	}, [resolvedSubjectId, service]);

	useEffect(() => {
		const controller = new AbortController();
		load({ signal: controller.signal }).catch(() => {});
		return () => controller.abort();
	}, [load, attempt]);

	useEffect(() => {
		if (typeof onCountChangeRef.current === 'function') onCountChangeRef.current(attachments.length);
	}, [attachments.length]);

	// Runs before antd hands the file to customRequest; returning LIST_IGNORE
	// rejects it without leaving a ghost row in the (hidden) upload list.
	const precheckUpload = useCallback((file) => {
		if (isBlockedFileName(file?.name)) {
			message.error(`${file?.name || 'That file'} is a file type keepr does not accept.`);
			return Upload.LIST_IGNORE;
		}
		// attach-admins upload past the default ceiling; the server decides.
		if (!isAttachAdmin() && Number(file?.size) > ATTACHMENT_MAX_BYTES) {
			message.error(`Attachments are limited to ${ATTACHMENT_MAX_MB} MB.`);
			return Upload.LIST_IGNORE;
		}
		return true;
	}, [message]);

	const handleUpload = useCallback(async ({ file, onSuccess, onError }) => {
		if (!resolvedSubjectId || !service) {
			onError?.(new Error('No subject to attach to.'));
			return;
		}
		setUploading(true);
		try {
			const created = await service.upload(resolvedSubjectId, file);
			if (mountedRef.current && created) {
				// Newest first, matching the order the API lists them in.
				setAttachments(prev => [created, ...prev]);
			}
			message.success(`${file?.name || 'File'} attached.`);
			onSuccess?.(created);
		} catch (error) {
			console.error(error);
			message.error(serverMessage(error) || 'Unable to attach that file.');
			onError?.(error);
		} finally {
			if (mountedRef.current) setUploading(false);
		}
	}, [resolvedSubjectId, service, message]);

	const handleDownload = useCallback(async (attachment, event) => {
		const anchor = anchorOf(event);
		if (!resolvedSubjectId || !service) return;
		if (!(await confirmFlaggedFile(modal, attachment))) return;
		const id = normalizeId(attachment?._id);
		setBusyId(id);
		try {
			await service.download(resolvedSubjectId, id, attachment?.originalName, { anchor });
		} catch (error) {
			console.error(error);
			message.error(serverMessage(error) || 'Unable to download that file.');
		} finally {
			if (mountedRef.current) setBusyId(null);
		}
	}, [resolvedSubjectId, service, message, modal]);

	const handleDownloadAll = useCallback(async (event) => {
		const anchor = anchorOf(event);
		if (!resolvedSubjectId || !service || !attachments.length) return;
		if (!(await confirmFlaggedFiles(modal, attachments))) return;
		setDownloadingAll(true);
		message.info(`Downloading ${attachments.length} file${attachments.length === 1 ? '' : 's'}…`);
		let failures = 0;
		for (let idx = 0; idx < attachments.length; idx += 1) {
			const attachment = attachments[idx];
			try {
				// eslint-disable-next-line no-await-in-loop
				await service.download(
					resolvedSubjectId,
					normalizeId(attachment?._id),
					attachment?.originalName,
					{ anchor }
				);
			} catch (error) {
				console.error(error);
				failures += 1;
			}
			// eslint-disable-next-line no-await-in-loop
			if (idx < attachments.length - 1) await wait(DOWNLOAD_ALL_DELAY_MS);
		}
		if (mountedRef.current) setDownloadingAll(false);
		if (failures) {
			message.error(`${failures} file${failures === 1 ? '' : 's'} could not be downloaded.`);
		}
	}, [resolvedSubjectId, service, attachments, message, modal]);

	const handleRemove = useCallback(async (attachment) => {
		if (!resolvedSubjectId || !service) return;
		const id = normalizeId(attachment?._id);
		setBusyId(id);
		try {
			await service.remove(resolvedSubjectId, id);
			// Its thumbnail and viewer sizes go with it.
			forgetAttachmentVariants(id);
			if (mountedRef.current) {
				setAttachments(prev => prev.filter(row => normalizeId(row?._id) !== id));
				// A file that no longer exists cannot stay open in the preview.
				setPreviewing(prev => (normalizeId(prev?._id) === id ? null : prev));
			}
			message.success('Attachment removed.');
		} catch (error) {
			console.error(error);
			message.error(serverMessage(error) || 'Unable to remove that attachment.');
		} finally {
			if (mountedRef.current) setBusyId(null);
		}
	}, [resolvedSubjectId, service, message]);

	// Whether this viewer may change the list at all: write access, and a
	// record that is not locked.
	const changeable = !readOnly && !locked;
	const canAttach = Boolean(resolvedSubjectId) && changeable && Boolean(service);
	// The viewer and the thumbnails read item-scoped routes, so they are
	// offered for the subject type that has them. A reader who may take copies
	// opens every file (the viewer reads the download endpoint, or offers
	// Download for what it cannot show); a reader who may not opens only a
	// photo that has its 2,048 px `lg` size — looking, which the variants
	// route allows every reader of the item. Reading is reading — readOnly
	// doesn't remove it, exactly as it doesn't remove Download.
	const hasViewer = subjectType === 'item' && Boolean(resolvedSubjectId);
	const canPreview = attachment => hasViewer && canOpenAttachment(attachment, { canDownload });

	// One set of upload props for both pickers: the dropzone and the
	// empty-line's "Attach a file" link both hand files to the same request.
	const uploadProps = {
		showUploadList: false,
		beforeUpload: precheckUpload,
		customRequest: handleUpload,
		disabled: uploading,
		multiple: true,
	};

	// One muted line with the action inline (B3) — no illustration.
	// The link is a second trigger for the same upload request.
	const emptyLine = (
		<div className="empty-line">
			<span>No attachments</span>
			{canAttach ? (
				<>
					<span aria-hidden="true">·</span>
					<Upload {...uploadProps}>
						<Button type="link">Attach a file</Button>
					</Upload>
				</>
			) : null}
		</div>
	);
	const hasData = loadedFor === resolvedSubjectId && loadedFor !== null;
	// Before the first read has even started there is nothing to say yet: never "No attachments".
	const waiting = loading || (!hasData && !loadError);

	return (
		// The section renders INSIDE the item form, whose antd <Form disabled>
		// would otherwise grey out its controls. Attachments don't belong to
		// the form's save lifecycle, and a read-only viewer must still be able
		// to download — `readOnly` is this section's own switch (it drops
		// Attach and Remove), so the inherited disabled state is reset here.
		<ConfigProvider componentDisabled={false}>
		<div className={`attachments${variant === 'embedded' ? ' attachments--embedded' : ''}`}>
			<div className="attachments__header">
				{variant === 'embedded' || !title ? null : (
					<div className="micro-label attachments__title">{title}</div>
				)}
				{/* Zero is not information: Download all exists only once there
				    is something to download. */}
				{attachments.length && canDownload ? (
					<div className="attachments__actions">
						<Button
							size="small"
							icon={<DOWNLOAD />}
							disabled={downloadingAll}
							loading={downloadingAll}
							onClick={handleDownloadAll}
						>
							Download all
						</Button>
					</div>
				) : null}
			</div>

			{/* The dropzone is the affordance — no Attach button. Before the
			    item exists the reason lives in the zone itself, never a bare
			    disabled control (platform form spec). */}
			{!resolvedSubjectId ? (
				<div className="attachments__pending-zone">
					Attachments save with the item — save it first, then drop files here.
				</div>
			) : null}

			{/* A locked record keeps the zone's place and says why there, for a
			    viewer who could otherwise change the list (a reader never could,
			    so is told nothing). */}
			{resolvedSubjectId && locked && !readOnly ? (
				<div className="attachments__pending-zone">
					Locked with the record — a manager can unlock it to add or remove files.
				</div>
			) : null}

			{canAttach ? (
				<Upload.Dragger {...uploadProps} className="attachments__dropzone" aria-label="Attach files">
					<span className="attachments__dropzone-title">
						<PAPERCLIP aria-hidden="true" /> Drop files here
					</span>
					<span className="attachments__dropzone-hint">
						{uploading ? 'Uploading…' : 'or choose files'}
					</span>
				</Upload.Dragger>
			) : null}

			{resolvedSubjectId ? (
				<DataState
					className="attachments__state"
					state={deriveDataState({ loading: waiting, error: loadError, hasData, isEmpty: !attachments.length })}
					hasData={hasData}
					isEmpty={!attachments.length}
					skeleton={<Skeleton variant="list-row" count={2} />}
					empty={emptyLine}
					error={loadError}
					onRetry={() => setAttempt(n => n + 1)}
					label="attachments"
				>
					<List
						className="attachments__list"
						size="small"
						dataSource={attachments}
						rowKey={row => normalizeId(row?._id)}
						renderItem={(attachment) => {
							const id = normalizeId(attachment?._id);
							const ext = (attachment?.ext || getFileExtension(attachment?.originalName) || '')
								.replace(/^\./, '');
							const TypeIcon = getFileTypeIcon(ext);
							const name = attachment?.originalName || id;
							const added = attachment?.createdAt ? formatShortDate(attachment.createdAt) : '';
							const opens = canPreview(attachment);
							return (
								// Two lines, because the panel is often a narrow column:
								// the NAME gets the full width of the first one (it is the
								// only part a reader scans), and the facts about the file
								// — type, size, when it arrived — sit under it. The icon
								// rides the name's line and the actions the right edge.
								<List.Item className="attachments__row">
									<AttachmentRowPicture
										itemId={hasViewer ? resolvedSubjectId : null}
										attachment={attachment}
										TypeIcon={TypeIcon}
										onOpen={opens ? () => setPreviewing(attachment) : null}
									/>
									<div className="attachments__main">
										{opens ? (
											// A link-styled Button, not a Typography.Link: an
											// anchor with no href is unreachable by keyboard,
											// and this is the row's primary action.
											<Button
												type="link"
												className="attachments__name"
												title={name}
												aria-label={`Preview ${name}`}
												onClick={() => setPreviewing(attachment)}
											>
												{name}
											</Button>
										) : (
											<span className="attachments__name" title={name}>
												{name}
											</span>
										)}
										<div className="attachments__meta">
											{ext ? <Tag className="attachments__ext">{ext.toUpperCase()}</Tag> : null}
											{/* Size and date are ONE node, joined the platform's
											    way: in a narrow panel the line wraps under the
											    type tag as a unit rather than leaving a stranded
											    separator on a line of its own. */}
											<Text type="secondary" className="attachments__facts">
												{joinMeta(formatBytes(attachment?.size), added)}
											</Text>
										</div>
										{/* What strangers took through public links (PR 10c) —
										    the API sends it to the collection's managers only. */}
										<FileScanNote file={attachment} className="attachments__scan" />
										{publicDownloadLines(attachment?.publicDownloads).map((line, index) => (
											<Text key={`${index}-${line}`} type="secondary" className="attachments__public-downloads">{line}</Text>
										))}
									</div>
									{/* Small, as the related-items row's remove is: every
									    pixel the actions take is a pixel off the name beside
									    them, and in a 250px panel that is the difference
									    between the facts sharing a line and wrapping. */}
									<span className="attachments__row-actions">
										{canDownload ? (
											<Button
												type="text"
												size="small"
												icon={<DOWNLOAD />}
												aria-label={`Download ${name}`}
												title="Download"
												loading={busyId === id}
												onClick={event => handleDownload(attachment, event)}
											/>
										) : null}
										{!changeable || !isOtherAttachment(attachment) ? null : (
											<Popconfirm
												title="Remove this attachment?"
												description="You can restore it from History for 30 days."
												{...destructivePopconfirmProps('Remove')}
												onConfirm={() => handleRemove(attachment)}
											>
												<Button
													type="text"
													size="small"
													danger
													icon={<CLOSE />}
													aria-label={`Remove ${name}`}
													title="Remove attachment"
												/>
											</Popconfirm>
										)}
									</span>
								</List.Item>
							);
						}}
					/>
				</DataState>
			) : null}

			{hasViewer ? (
				<AttachmentPreviewModal
					attachment={previewing}
					itemId={resolvedSubjectId}
					canDownload={canDownload}
					open={Boolean(previewing)}
					onClose={() => setPreviewing(null)}
				/>
			) : null}
		</div>
		</ConfigProvider>
	);
}
